import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { http } from '../../api/http';
import { useAuthStore } from '../../store/auth.store';
import './landing-v2.css';
import './account-access.css';

export default function PasswordPage({ mode }: { mode: 'forgot' | 'reset' | 'change' }) {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') || '');
  // Lien d'invitation d'un compte de l'équipe (J1) : même lien, page de bienvenue.
  const [welcome] = useState(() => mode === 'reset' && new URLSearchParams(window.location.hash.slice(1)).has('bienvenue'));
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (mode === 'reset' && window.location.hash) window.history.replaceState(window.history.state, '', window.location.pathname);
  }, [mode]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;
    const data = new FormData(event.currentTarget);
    const value = (name: string) => String(data.get(name) || '');
    setError('');
    if (mode !== 'forgot' && value('newPassword') !== value('confirmation')) {
      setError('Les mots de passe ne correspondent pas.'); return;
    }
    if (mode !== 'forgot' && new TextEncoder().encode(value('newPassword')).length > 72) {
      setError('Ce mot de passe est trop long (72 octets maximum).'); return;
    }
    setPending(true);
    try {
      await http.post(`/v1/auth/${mode === 'forgot' ? 'forgot' : mode === 'reset' ? 'reset' : 'change'}-password`,
        mode === 'forgot' ? { email: value('email').trim() } : mode === 'reset' ? { token, newPassword: value('newPassword') }
          : { currentPassword: value('currentPassword'), newPassword: value('newPassword') });
      setDone(true);
      if (mode === 'reset' && useAuthStore.getState().token) await useAuthStore.getState().logout();
      if (mode === 'change') {
        await useAuthStore.getState().logout();
        window.location.assign('/login?passwordChanged=1');
      }
    } catch (e) {
      const status = isAxiosError(e) ? e.response?.status : undefined;
      setError(status === 429 ? 'Trop de tentatives. Patientez 15 minutes avant de réessayer.'
        : status === 400 && mode === 'reset' ? 'Ce lien est invalide ou expiré. Demandez un nouveau lien.'
        : status === 400 && mode === 'change' ? 'Vérifiez votre mot de passe actuel et le nouveau mot de passe.'
        : 'Opération indisponible. Réessayez dans quelques instants.');
    } finally { setPending(false); }
  }
  const title = mode === 'forgot' ? 'Mot de passe oublié' : welcome ? 'Choisir votre mot de passe' : mode === 'reset' ? 'Choisir un nouveau mot de passe' : 'Changer mon mot de passe';
  return <div className="lp-v2 account-access">
    <main style={{ maxWidth: 540, margin: '48px auto', padding: 20 }}>
      <section className="account-access__card">
        {mode === 'change' && <Link to="/today">← Retour à mon espace</Link>}
        <h1>{title}</h1>
        {done ? <>
          <p role="status">{mode === 'forgot' ? 'Si un compte actif correspond à cette adresse, vous recevrez un lien valable 30 minutes. Pensez à vérifier vos courriers indésirables.' : welcome ? 'Votre mot de passe est enregistré. Connectez-vous avec votre adresse e-mail et ce mot de passe.' : 'Votre mot de passe a été modifié. Reconnectez-vous avec votre nouveau mot de passe.'}</p>
          <Link to="/login">Retour à la connexion</Link>
        </> : mode === 'reset' && !token ? <>
          <p role="alert">Ouvrez le lien reçu par email pour réinitialiser votre mot de passe.</p>
          <Link to="/forgot-password">Demander un nouveau lien</Link>
        </> : <>
          <p>{mode === 'forgot' ? 'Indiquez l’adresse email de votre compte Permio.' : welcome ? 'Bienvenue sur Permio. Choisissez le mot de passe de votre compte.' : 'Après validation, vous devrez vous reconnecter sur vos appareils.'}</p>
          <form onSubmit={submit} aria-busy={pending}>
            <fieldset disabled={pending}>
              {mode === 'forgot' ? <label>Email<input name="email" type="email" autoComplete="email" required maxLength={255} /></label> : <>
                {mode === 'change' && <label>Mot de passe actuel<input name="currentPassword" type="password" autoComplete="current-password" required maxLength={128} /></label>}
                <label>Nouveau mot de passe<input name="newPassword" type="password" autoComplete="new-password" required minLength={12} maxLength={72} /></label>
                <small>12 caractères minimum.</small>
                <label>Confirmer le nouveau mot de passe<input name="confirmation" type="password" autoComplete="new-password" required minLength={12} maxLength={72} /></label>
              </>}
              {error && <p className="account-access__error" role="alert">{error}</p>}
              <button type="submit" className="btn-hero account-access__submit">{pending ? 'Veuillez patienter…' : mode === 'forgot' ? 'Recevoir le lien' : 'Enregistrer le mot de passe'}</button>
            </fieldset>
          </form>
          <p><Link to={mode === 'reset' || mode === 'change' ? '/forgot-password' : '/login'}>{mode === 'change' ? 'Mot de passe actuel oublié ?' : mode === 'reset' ? 'Demander un nouveau lien' : 'Retour à la connexion'}</Link></p>
        </>}
      </section>
    </main>
  </div>;
}
